import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const CancellationRefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Cancellation and Refund Policy"
        description="Learn about our cancellation and refund policies for EmailValidator services. Understand your rights and our procedures for subscription cancellations and refunds."
        keywords="cancellation policy, refund policy, email validation refunds, subscription cancellation, refund terms, cancellation terms"
        canonical="https://mailvalidator.octalchip.com/cancellation-refund"
      />
      
      <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-4">
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Cancellation and Refund Policy</h1>
            <p className="text-gray-600">Last updated: December 17, 2024</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <section id="subscription-cancellation">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Subscription Cancellation</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You may cancel your EmailValidator subscription at any time through your account settings or by contacting our support team.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>How to Cancel:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Log into your EmailValidator account</li>
                <li>Go to Account Settings → Subscription</li>
                <li>Click "Cancel Subscription"</li>
                <li>Confirm your cancellation</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>Alternative Method:</strong> Contact our support team at{' '}
                <a href="mailto:support@emailvalidator.com" className="text-primary hover:underline">
                  support@emailvalidator.com
                </a>
              </p>
            </section>

            <section id="refund-policy">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Refund Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>30-Day Money-Back Guarantee:</strong> We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied with our service within the first 30 days of your subscription, we'll provide a full refund.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Refund Eligibility:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Refund requests must be made within 30 days of initial purchase</li>
                <li>Only the first payment of a subscription is eligible for refund</li>
                <li>Refunds are not available for renewals or upgrades</li>
                <li>Enterprise plans may have different refund terms as specified in your contract</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>How to Request a Refund:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Contact our support team at support@emailvalidator.com</li>
                <li>Include your account email and reason for refund</li>
                <li>We'll review your request and respond within 3 business days</li>
              </ul>
            </section>

            <section id="after-cancellation">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. What Happens After Cancellation</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Immediate Effects:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Your subscription will be cancelled at the end of the current billing period</li>
                <li>You'll continue to have access to your plan features until the end of the paid period</li>
                <li>No further charges will be made to your account</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Data Retention:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Your validation history and data will be retained for 90 days after cancellation</li>
                <li>You can export your data during this period</li>
                <li>After 90 days, your data may be permanently deleted</li>
              </ul>
            </section>

            <section id="pro-rated-refunds">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Pro-Rated Refunds</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Annual Subscriptions:</strong> If you cancel an annual subscription before the end of the year, you may be eligible for a pro-rated refund based on the unused portion of your subscription.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Calculation Method:</strong> Pro-rated refunds are calculated based on the number of full months remaining in your subscription period.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Processing Time:</strong> Refunds are typically processed within 5-10 business days and will be credited back to your original payment method.
              </p>
            </section>

            <section id="refund-exceptions">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Exceptions</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>No Refunds For:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Free plans or trial periods</li>
                <li>Violations of our Terms of Service</li>
                <li>Fraudulent activity or abuse of our services</li>
                <li>Enterprise plans with custom contracts (subject to contract terms)</li>
              </ul>
            </section>

            <section id="refund-contact">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                For questions about cancellations or refunds, please contact us:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:support@emailvalidator.com" className="text-primary hover:underline">
                    support@emailvalidator.com
                  </a>
                </li>
                <li>
                  <strong>Phone:</strong>{' '}
                  <a href="tel:+917574082582" className="text-primary hover:underline">
                    +91 7574082582
                  </a>
                </li>
                <li>
                  <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM IST
                </li>
              </ul>
            </section>

            <section id="refund-policy-changes">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify this Cancellation and Refund Policy at any time. Changes will be effective immediately upon posting on our website. We will notify users of any material changes via email or through our website.
              </p>
            </section>
          </div>
        </div>
        <Footer />
    </div>
  );
};

export default CancellationRefundPolicy; 